import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpotify } from 'react-icons/fa';
import { ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { albumLabel, formatDate, formatDuration, isYou, writerName } from '../derive-ui';
import type { PublishingArtist, PublishingTrack, PublishingTrackState } from 'types/publishing';
import { ui } from '../ui';

interface Props {
  track: PublishingTrack | null;
  artist: PublishingArtist;
  /** Eligible for Contact us (not listed, not sent, not hidden): shows the primary register button. */
  canRegister: boolean;
  canNotMine: boolean;
  canRestore: boolean;
  /** Admin viewing a foreign artist: actions shown but disabled. */
  readOnly: boolean;
  onClose: () => void;
  onRegister: () => void;
  onNotMine: () => void;
  onRestore: () => void;
}

// Design `tone`: registered → green, unknown → amber, no_ipi → gray.
const toneClass: Record<PublishingTrackState, string> = {
  listed: 'border-transparent bg-green-100 text-green-800 hover:bg-green-100',
  not_listed: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
  cant_check: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-100',
};

export function TrackDialog({
  track,
  artist,
  canRegister,
  canNotMine,
  canRestore,
  readOnly,
  onClose,
  onRegister,
  onNotMine,
  onRestore,
}: Props) {
  const { t } = useTranslation('publishing');
  if (!track) return null;
  const work =
    track.registeredAs && track.registeredAs.toLowerCase() !== track.title.toLowerCase()
      ? track.registeredAs
      : '';
  const rows: [string, string][] = (
    [
      [
        t('detail_release'),
        [track.kind, formatDate(track.releaseDate)].filter(Boolean).join(' · '),
      ],
      [t('detail_album'), albumLabel(track) || ''],
      [t('detail_length'), formatDuration(track.durationSeconds)],
      [t('detail_registered_as'), work],
    ] as [string, string][]
  ).filter((r) => r[1]);
  const writers = track.writers || [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px] gap-0 p-6">
        {/* header: title + artist; the X in the corner comes from DialogContent */}
        <div className="flex items-start justify-between gap-3 pr-6">
          <div className="min-w-0">
            <DialogTitle className={`mb-1 ${ui.subhead}`}>{track.title}</DialogTitle>
            <DialogDescription className={ui.muted}>{artist.artistName}</DialogDescription>
          </div>
          {track.spotifyTrackId && (
            <a
              className={`inline-flex shrink-0 items-center gap-1.5 ${ui.link}`}
              href={'https://open.spotify.com/track/' + track.spotifyTrackId}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaSpotify size={16} className="shrink-0 text-[#1DB954]" aria-hidden />
              {t('detail_listen')}
            </a>
          )}
        </div>

        {/* status */}
        <div className="mt-3 mb-4">
          <Badge className={`text-sm font-medium ${toneClass[track.state]}`}>
            {t(`pill_${track.state}`)}
          </Badge>
          <p className={`m-0 mt-2 leading-relaxed ${ui.muted}`}>{t(`why_${track.state}`)}</p>
        </div>

        {/* meta card (design .sf-meta / .sf-metarow) */}
        {!!rows.length && (
          <div className="mb-4 box-border rounded-[10px] border border-[#E9E9E9] px-3.5 py-0.5">
            {rows.map(([k, v], i) => (
              <div
                key={k}
                className={`flex items-baseline justify-between gap-4 py-2.5 ${
                  i ? 'border-t border-[#F0F0F0]' : ''
                }`}
              >
                <span className={ui.muted}>{k}</span>
                <span className={`text-right font-medium ${ui.body}`}>{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* writers */}
        {!!writers.length && (
          <div className="mb-4">
            <div className={`mb-2 text-xs font-medium uppercase tracking-[.06em] ${ui.muted}`}>
              {t('detail_writers')}
            </div>
            <div className="flex flex-col gap-1">
              {writers.map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={ui.body}>{writerName(w.name)}</span>
                  {(w.isArtist || isYou(artist.legalName, w.name)) && (
                    <Badge className="border-transparent bg-blue-100 text-xs font-medium text-blue-800 hover:bg-blue-100">
                      {t('writers_you')}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* technical details, under the writers */}
        <details className="group mb-4">
          <summary
            className={`inline-flex cursor-pointer list-none items-center gap-1.5 text-sm [&::-webkit-details-marker]:hidden ${ui.muted}`}
          >
            <ChevronRight
              size={14}
              className="shrink-0 transition-transform group-open:rotate-90"
              aria-hidden
            />
            {t('detail_technical')}
          </summary>
          <div className={`mt-2 text-sm tabular-nums tracking-[0.03em] ${ui.muted}`}>
            {t('detail_songwriter_id', { id: artist.ipi || t('detail_none') })}
            <br />
            {t('detail_isrc', { isrc: track.isrc || t('detail_dash') })}
            {!!track.songCodes?.length && (
              <>
                <br />
                {t('detail_work_code', { code: track.songCodes.join(', ') })}
              </>
            )}
            {track.iswc && (
              <>
                <br />
                {t('detail_iswc', { iswc: track.iswc })}
              </>
            )}
          </div>
        </details>

        <Separator className="mb-3" />

        {/* footer: actions, stacked */}
        <div className="flex flex-col gap-2">
          {canNotMine && (
            <Button
              variant="outline"
              className={ui.btnSecondary}
              disabled={readOnly}
              onClick={onNotMine}
            >
              {t('not_mine')}
            </Button>
          )}
          {canRegister && (
            <Button className={ui.btnPrimary} disabled={readOnly} onClick={onRegister}>
              {t('register_label', { count: 1 })}
            </Button>
          )}
          {canRestore && (
            <Button
              variant="outline"
              className={ui.btnSecondary}
              disabled={readOnly}
              onClick={onRestore}
            >
              {t('restore', { count: 1 })}
            </Button>
          )}
          <Button variant="outline" className={ui.btnSecondary} onClick={onClose}>
            {t('detail_close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
